<template>
	<DialogControl v-model="open" max-width="640">
		<v-card rounded="xl" class="nav-dialog nav-profile">
			<v-card-title class="nav-dialog__title">
				<v-icon color="primary">manage_accounts</v-icon>
				<span>{{ t('My profile') }}</span>
				<v-spacer />
				<v-btn icon variant="text" size="small" @click="open = false">
					<v-icon>close</v-icon>
				</v-btn>
			</v-card-title>

			<v-tabs v-model="tab" color="primary" grow show-arrows density="compact">
				<v-tab v-for="item in tabs" :key="item.key" :value="item.key" :prepend-icon="item.icon">{{ t(item.labelKey) }}</v-tab>
			</v-tabs>
			<v-divider />

			<v-card-text class="nav-profile__body">
				<v-window v-model="tab">
					<v-window-item value="account">
						<div class="nav-profile__fields">
							<v-text-field :model-value="nav.user.userName" :label="t('UserName')" variant="outlined" readonly />
							<v-text-field :model-value="nav.user.firstName" :label="t('First name')" variant="outlined" readonly />
							<v-text-field :model-value="nav.user.lastName" :label="t('Last name')" variant="outlined" readonly />
							<v-text-field :model-value="nav.user.email" :label="t('Email')" variant="outlined" readonly />
							<v-text-field :model-value="nav.user.organisationLabel" :label="t('Organisation')" variant="outlined" readonly />
							<v-select v-model="language" :items="profile.languages" item-title="label" item-value="id" :label="t('Languages')" variant="outlined" :disabled="!nav.online" />
						</div>
					</v-window-item>

					<v-window-item value="password">
						<div class="nav-profile__fields">
							<v-text-field v-model="password.current" type="password" :label="t('Old password')" variant="outlined" autocomplete="current-password" />
							<v-text-field v-model="password.next" type="password" :label="t('New password')" variant="outlined" autocomplete="new-password" />
							<v-text-field v-model="password.confirm" type="password" :label="t('Confirm new password')" variant="outlined" autocomplete="new-password" />
							<v-alert v-if="passwordError" type="error" variant="tonal" density="compact">{{ t(passwordError) }}</v-alert>
						</div>
					</v-window-item>

					<v-window-item v-if="nav.agendaSynchAllowed" value="agenda">
						<div class="nav-profile__agenda">
							<div class="nav-profile__agenda-provider">
								<span>MICROSOFT OFFICE 365</span>
								<v-btn variant="tonal" prepend-icon="restart_alt" @click="resetAgenda">{{ t('ResetEXT') }}</v-btn>
							</div>
							<v-switch v-model="agenda.active" color="primary" :label="t('AgendaSynchON')" hide-details @update:model-value="onAgendaToggle" />
							<v-switch v-for="calendar in calendars" :key="calendar.id" v-model="agenda.activated" :value="calendar.id" color="primary" hide-details density="compact" :disabled="!agenda.active" :label="calendar.isDefault ? `${calendar.name} (${t('DefaultCalendar')})` : calendar.name" />
						</div>
					</v-window-item>

					<v-window-item value="options">
						<div class="nav-profile__fields">
							<v-text-field :model-value="profile.version" :label="t('Version')" variant="outlined" readonly />
							<v-text-field :model-value="nav.storageSize.sizeBase" :label="t('WebCache')" suffix="MB" variant="outlined" readonly />
							<v-text-field :model-value="nav.storageSize.sizeExtraSales" :label="t('WebCacheSales')" suffix="MB" variant="outlined" readonly />
							<v-text-field :model-value="nav.storageSize.sizeExtraDoc" :label="t('WebCacheDoc')" suffix="MB" variant="outlined" readonly />
							<v-switch v-model="allowDocSync" color="primary" :label="t('DOCUMENT_SYNC.')" hide-details @update:model-value="setDocSync" />
						</div>
					</v-window-item>

					<v-window-item v-if="showExtensions" value="extensions">
						<div v-for="extension in profile.extensions" :key="extension.extensionId" class="nav-profile__extension">
							<v-switch v-model="enabledExtensions" :value="extension.extensionId" color="primary" hide-details density="compact" :disabled="extension.mandatory" :label="t(`${extension.reference}_description`)" />
							<v-btn v-if="extension.hasSettings" icon variant="text" size="small" @click="openSettings(extension)">
								<v-icon>settings</v-icon>
							</v-btn>
						</div>
					</v-window-item>
				</v-window>
			</v-card-text>

			<v-card-actions>
				<v-btn v-if="tab === 'options'" variant="text" prepend-icon="refresh" @click="refreshStorageSize">{{ t('refresh') }}</v-btn>
				<v-spacer />
				<v-btn variant="text" @click="open = false">{{ t('Close') }}</v-btn>
				<v-btn color="primary" variant="tonal" @click="save">{{ t(saveLabel) }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>

	<NavConfirmDialog v-model="confirmLanguage" icon="translate" :title="t('SwitchLanguageTitle')" :lines="[t('RefreshAndLogoutForSwitchLanguage')]" :confirm-text="t('Refresh')" @confirm="logoutForLanguage(language)" />
	<NavConfirmDialog v-model="confirmStorage" icon="delete_sweep" :title="t('Confirmation')" :lines="[t('EmptyLocalCache'), t('EmptyLocalCache2')]" :confirm-text="t('Clear storage')" :cancel-text="t('No')" @confirm="clearLocalStorage" />
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import NavConfirmDialog from './NavConfirmDialog.vue';
import { legacyNav as nav } from '@/iframe/legacyNavState';
import { agendaSignIn, clearLocalStorage, logoutForLanguage, openExtensionSettings, readProfile, refreshStorageSize, resetExternalAgenda, saveAgenda, saveExtensions, saveLanguage, savePassword, setDocSync, showSaveSuccess } from '@/iframe/legacyNavActions';
import { useTranslator } from '@/composables/useTranslator';

const open = defineModel({ type: Boolean, default: false });
const t = useTranslator();

const tab = ref('account');
const profile = ref(readProfile());
const language = ref(null);
const allowDocSync = ref(true);
const enabledExtensions = ref([]);
const agenda = reactive({ active: false, activated: [] });
const password = reactive({ current: '', next: '', confirm: '' });
const passwordError = ref(null);
const confirmLanguage = ref(false);
const confirmStorage = ref(false);

const showExtensions = computed(() => nav.appIsReady && profile.value.extensions.length > 0);
const calendars = computed(() => (nav.agendaCalendars.length ? nav.agendaCalendars : profile.value.agenda.calendars));

const tabs = computed(() => [{ key: 'account', icon: 'account_circle', labelKey: 'Infos' }, { key: 'password', icon: 'lock', labelKey: 'Password' }, nav.agendaSynchAllowed && { key: 'agenda', icon: 'calendar_month', labelKey: 'SSO Agenda' }, { key: 'options', icon: 'settings', labelKey: 'Options' }, showExtensions.value && { key: 'extensions', icon: 'extension', labelKey: 'Extensions' }].filter(Boolean));

const saveLabel = computed(() => ({ password: 'Change password', options: 'Clear storage' })[tab.value] ?? 'SaveChanges');

const load = () => {
	profile.value = readProfile();
	language.value = profile.value.language;
	allowDocSync.value = profile.value.allowDocSync;
	enabledExtensions.value = profile.value.extensions.filter((extension) => extension.enabled).map((extension) => extension.extensionId);
	agenda.active = profile.value.agenda.active;
	agenda.activated = [...profile.value.agenda.activated];
	Object.assign(password, { current: '', next: '', confirm: '' });
	passwordError.value = null;
	if (!tabs.value.some((item) => item.key === tab.value)) tab.value = 'account';
};

watch(open, (value) => value && load());

const onAgendaToggle = (value) => {
	if (value) agendaSignIn();
};

const resetAgenda = () => {
	open.value = false;
	resetExternalAgenda();
};

const openSettings = (extension) => {
	open.value = false;
	openExtensionSettings(extension.extensionId);
};

const save = async () => {
	switch (tab.value) {
		case 'account':
			if (language.value && language.value !== profile.value.language && saveLanguage(language.value)) {
				open.value = false;
				confirmLanguage.value = true;
				return;
			}
			break;
		case 'password':
			passwordError.value = await savePassword(password);
			if (passwordError.value) return;
			break;
		case 'agenda':
			saveAgenda({ active: agenda.active, calendars: agenda.activated });
			showSaveSuccess();
			break;
		case 'options':
			open.value = false;
			confirmStorage.value = true;
			return;
		case 'extensions':
			saveExtensions(enabledExtensions.value);
			showSaveSuccess();
			break;
	}
	open.value = false;
};
</script>
